// Single home for "what order do classes appear in" and "which class is below
// the account-restriction cutoff" — the two rules that every class dropdown,
// the promotion page and the admin lockdown screen all need to agree on.
//
// WHY THIS EXISTS (Nigeria):
//  • A Nigerian school's classes are ordered tier first (Kindergarten < Nursery
//    < Primary < Highschool), then grade_level (the class's real position
//    inside its tier), then sort_order (only a tie-break, e.g. branches of the
//    same grade). Several screens used to order by tier + sort_order only, so
//    add-order beat real grade order ("Primary 3" added before "Primary 2"
//    showed first everywhere except the Classes page).
//  • grade_level is position-WITHIN-tier in Nigeria (Primary 1 and JSS 1 both
//    have grade_level 1), so it can never be compared across tiers. The
//    account lockdown cutoff therefore has to compare a tier-aware rank.
//
// Pakistan is untouched: every function here is a no-op / plain grade_level
// for any school whose country is not 'nigeria'.

import { supabase } from './supabase';
import { getCountryConfig } from './countryConfig';

export interface OrderableClass {
  tier?: number | null;
  grade_level?: number | null;
  sort_order?: number | null;
  name?: string | null;
}

const LAST = Number.MAX_SAFE_INTEGER;

// Nigeria ordering: tier (null = custom class, always after the templated
// tiers) → grade_level → sort_order → name.
export function compareClasses(a: OrderableClass, b: OrderableClass): number {
  const tierA = a.tier ?? LAST, tierB = b.tier ?? LAST;
  if (tierA !== tierB) return tierA - tierB;
  const gradeA = a.grade_level ?? LAST, gradeB = b.grade_level ?? LAST;
  if (gradeA !== gradeB) return gradeA - gradeB;
  const sortA = a.sort_order ?? LAST, sortB = b.sort_order ?? LAST;
  if (sortA !== sortB) return sortA - sortB;
  return (a.name || '').localeCompare(b.name || '', undefined, { numeric: true, sensitivity: 'base' });
}

// Sorts a class list the way this school's country expects. Returns a new
// array. Pakistan: sort_order only (exactly as before).
export function sortClassesForCountry<T extends OrderableClass>(
  list: T[],
  country: string | null | undefined,
): T[] {
  const copy = list.slice();
  if (getCountryConfig(country as any).ordersClassesByTier) return copy.sort(compareClasses);
  return copy.sort((a, b) => (a.sort_order ?? LAST) - (b.sort_order ?? LAST));
}

// Keeps classes.sort_order in step with the real tier/grade order.
//
// Some server-side logic (promote_students, the branch "representative =
// lowest sort_order" rule) reads sort_order directly. Add-order used to leave
// it out of step with the tier/grade order the screens display, which made
// promotion move students into the wrong class. Run this before anything that
// relies on sort_order (promotion, class list load). It only writes rows whose
// value actually changes, and never touches Pakistan schools.
export async function healClassSortOrder(
  schoolId: string,
  country: string | null | undefined,
): Promise<number> {
  if (!getCountryConfig(country as any).ordersClassesByTier) return 0;
  const { data, error } = await supabase
    .from('classes')
    .select('id, name, tier, grade_level, sort_order')
    .eq('school_id', schoolId)
    .eq('is_active', true);
  if (error || !data) return 0;
  const ordered = (data as any[]).slice().sort(compareClasses);
  const changed = ordered
    .map((c, idx) => ({ id: c.id, idx, current: c.sort_order }))
    .filter(r => r.current !== r.idx);
  if (changed.length === 0) return 0;
  await Promise.all(changed.map(r =>
    supabase.from('classes').update({ sort_order: r.idx }).eq('id', r.id)
  ));
  return changed.length;
}

// ─── Account lockdown (restriction cutoff) ───────────────────────────────
//
// schools.account_restriction_cutoff is a single integer. Pakistan compares it
// with classes.grade_level (a flat 1..n position) — unchanged. Nigeria's
// grade_level restarts inside every tier, so a Nigerian cutoff is stored as a
// tier-aware rank instead: (tier + 1) * 100 + grade_level. Custom (tier-less)
// classes use tier slot 9, i.e. they sit after every templated class — the
// same place they appear in every class list.

export const NIGERIA_RANK_BASE = 100;

export function classRestrictionRank(
  c: { tier?: number | null; grade_level?: number | null } | null | undefined,
  country: string | null | undefined,
): number {
  const grade = c?.grade_level ?? 0;
  if (country !== 'nigeria') return grade;
  const tierSlot = (c?.tier ?? 9) + 1;
  return tierSlot * NIGERIA_RANK_BASE + grade;
}

// A Nigerian cutoff saved before this fix is a bare grade_level (< 100) that
// never meant anything across tiers. Treat it as "not set" so nobody is locked
// out by an accident; the admin re-picks the class once and it's stored in the
// new form.
export function effectiveCutoff(
  cutoff: number | null | undefined,
  country: string | null | undefined,
): number | null {
  if (cutoff == null) return null;
  if (country === 'nigeria' && cutoff < NIGERIA_RANK_BASE) return null;
  return cutoff;
}

export function isLegacyNigeriaCutoff(
  cutoff: number | null | undefined,
  country: string | null | undefined,
): boolean {
  return country === 'nigeria' && cutoff != null && cutoff < NIGERIA_RANK_BASE;
}

// True when a class sits below the school's cutoff (its students get no login).
export function isClassBelowCutoff(
  c: { tier?: number | null; grade_level?: number | null } | null | undefined,
  cutoff: number | null | undefined,
  country: string | null | undefined,
): boolean {
  const eff = effectiveCutoff(cutoff, country);
  if (eff == null || !c) return false;
  return classRestrictionRank(c, country) < eff;
}
